/** @file Mirrors deform keys: paired meshes need a mirrorVertexMap (else dropped), unpaired keep theirs. */
import type { AnimationDoc, DeformKey, Doc, Id, MeshAtt } from '@pose2d/format/types';
import { throwError } from '@pose2d/format/throwError';
import { toPose2DError } from '@pose2d/format/toPose2DError';
import { invertPermutation } from '../mesh/invertPermutation';
import { copyJson } from './copyJson';
import { meshOffsets } from './meshOffsets';
import type { SideCounterparts } from './sideCounterparts';

/**
 * The inverse of the map `mesh` declares, through the shared mesh/invertPermutation (W2-29): a map that is not a
 * permutation of its `n` vertices is the command's VALIDATION at `source`, naming the mesh and the repair.
 */
function inverseOf(mesh: MeshAtt, map: readonly number[], n: number): number[] {
  try {
    return invertPermutation(map, n, 'source');
  } catch (e) {
    const err = toPose2DError(e);
    if (err === null) throw e;
    const hint = `the mirrorVertexMap of ${mesh.name} is not a permutation of its ${n} vertices (${err.hint ?? err.message}); set it again with mesh.setMirrorVertexMap`;
    return throwError('VALIDATION', undefined, { path: 'source', hint });
  }
}

/** Source vertex → target vertex: the source's own map, else the inverse of the counterpart's; null without a map. */
function vertexMap(doc: Doc, from: Id, to: Id): readonly number[] | null {
  const a = doc.attachments[from];
  const b = doc.attachments[to];
  if (a?.type !== 'mesh' || b?.type !== 'mesh' || a.uvs.length !== b.uvs.length) return null;
  const n = a.uvs.length / 2;
  if (a.mirrorVertexMap !== undefined) {
    inverseOf(a, a.mirrorVertexMap, n);
    return a.mirrorVertexMap;
  }
  return b.mirrorVertexMap === undefined ? null : inverseOf(b, b.mirrorVertexMap, n);
}

/** One deform key moved vertex by vertex onto the target mesh (values verbatim, re-trimmed; all zeros → null). */
function remapKey(key: DeformKey, map: readonly number[]): DeformKey {
  const size = map.length * 2;
  const from = meshOffsets.expand(key.offsets, size);
  const to = new Array<number>(size).fill(0);
  map.forEach((j, i) => {
    to[2 * j] = from[2 * i] ?? 0;
    to[2 * j + 1] = from[2 * i + 1] ?? 0;
  });
  return { ...copyJson(key), offsets: meshOffsets.trim(to) };
}

/** Deform timelines moved to the counterpart meshes, plus the source attachment ids whose keys were dropped. */
export function mirrorDeform(doc: Doc, deform: AnimationDoc['deform'], sides: SideCounterparts): { deform: AnimationDoc['deform']; dropped: Id[] } {
  const out: AnimationDoc['deform'] = {};
  const dropped: Id[] = [];
  for (const [attId, keys] of Object.entries(deform)) {
    const to = sides.attachment(attId);
    if (to === attId) {
      out[attId] = copyJson(keys);
      continue;
    }
    const map = vertexMap(doc, attId, to);
    if (map) out[to] = keys.map((k) => remapKey(k, map));
    else dropped.push(attId);
  }
  return { deform: out, dropped };
}
